import { useRef, useState, useEffect, useLayoutEffect, FunctionComponent } from "react";
import { useQuery } from 'react-query';
import cn from "classnames";
import { useDebounce } from 'use-debounce';
import DropdownSimple from "@components/DropdownSimple";
import TextInput from "@components/TextInput/TextInput";
// import useOutsideClick from "@hooks/useOutsideClick";
import { Loader } from "@components/new/Loader";
import SearchIcon from "@assets/icons/outline/search.svg?react";
import styles from "./SearchInput.module.scss";

export const SearchableString = ({ value, search }: {
  value: string,
  search?: string,
}) => {
  const res = (search ? value.split(search) : [value]);
  return (
    <>
      {res.map((v: string, idx: number) => (
        <>
          {v}
          {(idx < res.length - 1) && (
            <b>{search ?? ""}</b>
          )}
        </>
      ))}
    </>
  )
}

function renderItemDefault({ item, search, labelOptionItem }: {
  item: any,
  search?: string,
  labelOptionItem: (item: any, search: string) => string,
}) {
  const s = labelOptionItem(item, search ?? "");
  return (<SearchableString value={s} search={search} />);
}

function labelSelectedItemDefault(_: any, search: string) {
  return search;
}

export interface SearchInputProps<T> {
  // value?: T;
  className?: string;
  placeholder?: string;
  defaultSearchString?: string;
  getItems: (search: string) => Promise<T[]>;
  requestKey: string;
  keyExtractor: (item: T) => string;
  labelOptionItem?: (item: T | null, search: string) => string;
  // renderSelectedItem?: (props: { item: T | null }) => React.ReactNode;
  labelSelectedItem?: (item: T | null, search: string) => string;
  ItemComponent?: FunctionComponent<{ item: T, search?: string }>;
  MenuComponent?: FunctionComponent<{ items: T[], search?: string, isLoading?: boolean, onClickItem: (v: T[]) => void }>;
  onItemSelect?: (data: T[]) => void;
  disabled?: boolean;
  noIcon?: boolean;
  minLength?: number;
  // showSelected?: boolean;
}

const SearchInput = <T extends object>(props: SearchInputProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  // const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [isPristine, setIsPristine] = useState<boolean>(true);
  const [searchString, setSearchString] = useState<string>(props.defaultSearchString ?? "");
  const [activeSearchString] = useDebounce(searchString, 500);
  const [dropdownWidth, setDropdownWidth] = useState<number>(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // useOutsideClick<HTMLDivElement>(dropdownRef, () => setIsOpen(false));
  const { ItemComponent, MenuComponent } = props;

  const { data, isFetching } = useQuery({
    queryKey: [props.requestKey, activeSearchString],
    queryFn: () => props.getItems(activeSearchString),
    enabled: activeSearchString.length >= (props.minLength ?? 0),
    retry: 1,
    // onSuccess: (data: T[]) => (data?.length && setIsOpen(true)),
  });

  useEffect(() => {
    if (searchString && !isPristine && searchString?.length >= (props.minLength ?? 0)) {
      setIsOpen(true);
    }
  }, [searchString, isPristine])

  const handleSelect = (items: T[]) => {
    // setSelectedItem(item);
    props.onItemSelect && props.onItemSelect(items);
    setIsOpen(false);
  };
  const getItemLabel = props.labelOptionItem ?? labelSelectedItemDefault

  useLayoutEffect(() => {
    if (dropdownRef.current) {
      const { offsetWidth } = dropdownRef.current;
      setDropdownWidth(offsetWidth);
    }
  }, [dropdownRef.current?.offsetWidth]);

  return (
    <div className={cn(styles.search, props.className)} ref={dropdownRef}>
      <DropdownSimple
        triggerContent={(
          <TextInput
            className={styles.input}
            placeholder={props.placeholder}
            type="text"
            value={searchString}
            onChange={(v) => {
              setSearchString(v);
              setIsPristine(false);
            }}
            prefix={props.noIcon ? undefined : <SearchIcon />}
            onFocus={() => setIsOpen(true)}
            onClick={(e: any) => e.stopPropagation()}
            // autoFocus
          />
        )}
        dropdownContent={(
          <div
            className={styles.menu}
            style={{ width: dropdownWidth }}
          >
            {MenuComponent ? (
              <MenuComponent
                items={data ?? []}
                search={activeSearchString}
                onClickItem={(v: T[]) => handleSelect(v)}
                isLoading={isFetching}
              />
            ) : (isFetching ? (
              <Loader className={styles.loader} />
            ) : (data || []).map((item) => (
              <div
                key={props.keyExtractor(item)}
                className={styles.item}
                onClick={() => handleSelect([item])}
              >
                {(ItemComponent
                  ? (<ItemComponent item={item} search={activeSearchString} />)
                  : renderItemDefault({
                    item,
                    search: activeSearchString,
                    labelOptionItem: getItemLabel,
                  })
                )}
              </div>
            )))}
          </div>
        )}
        menuAlign="left"
        menuPosition="bottom"
        disabled={props.disabled}
        // menuWidth,
        // fixed
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </div>
  );
};

export default SearchInput;
